import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Check, Lock } from 'lucide-react'
import { signupRegister, type SignupRequest } from '../services/api'
import HelpPopover from '../components/help/HelpPopover'
import { OFFER_PHRASE, TALK_TO_US_HREF } from '../constants/offer'

/**
 * Signup — account creation, matching the redesigned mockup (Fig41).
 *
 * Collects the full profile the design specifies (name, work email, a
 * rule-checked password with a live strength meter, optional organisation +
 * country, and a starting-plan choice) rather than the previous bare
 * email/password/confirm form. Free is the recommended default and needs no
 * card; upgrades happen later from Account › Billing (Stripe-hosted).
 */
export default function Signup() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [organisation, setOrganisation] = useState('')
  const [country, setCountry] = useState('Australia')
  const [plan, setPlan] = useState<'free' | 'pro'>('free')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const errorRef = useRef<HTMLDivElement>(null)

  // Story #6766: a problem is said on the page, where it can be read and
  // reached by a screen reader, and brought into view on a long form.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [error])

  // Live password-policy check — mirrors the Cognito pool policy the mockup lists.
  const rules = [
    { key: 'length', label: 'At least 12 characters', ok: password.length >= 12 },
    { key: 'upper', label: 'One upper-case letter', ok: /[A-Z]/.test(password) },
    { key: 'number', label: 'One number', ok: /[0-9]/.test(password) },
    { key: 'symbol', label: 'One symbol (e.g. ! # @)', ok: /[^A-Za-z0-9]/.test(password) },
  ]
  const rulesMet = rules.filter((r) => r.ok).length
  const allRulesMet = rulesMet === 4
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch

  const strengthLabel = allRulesMet ? 'Strong password' : rulesMet >= 2 ? 'Getting there' : 'Weak password'
  const strengthColor = allRulesMet ? 'text-green-600' : rulesMet >= 2 ? 'text-amber-600' : 'text-red-500'

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (!name.trim()) return setError('Please enter your full name')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError('Please enter a valid email address')
    if (!allRulesMet) {
      const firstUnmet = rules.find((r) => !r.ok)
      return setError(`Your password is not strong enough yet. Still needed: ${firstUnmet?.label.toLowerCase()}.`)
    }
    if (!passwordsMatch) return setError("Passwords don't match — re-enter to confirm.")

    setLoading(true)
    try {
      const payload: SignupRequest = {
        // Free is the trial tier; Professional maps to the paid inventor tier
        // (payment is collected later via the Stripe-hosted billing portal).
        tier: plan === 'pro' ? 'pro_inventor' : 'trial',
        email,
        password,
        name: name.trim(),
        organisation: organisation.trim() || undefined,
        country: country.trim() || undefined,
      }
      const result = await signupRegister(payload)
      if (result.error) {
        setError(result.error)
        return
      }
      navigate('/login', { state: { registered: true } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-iflow-500 focus:ring-2 focus:ring-iflow-200'
  const labelClass = 'block text-sm font-semibold text-iflow-900 mb-1'

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-10 bg-gradient-to-br from-sky-50 via-white to-[#E8EFF4]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-[rgba(12,74,110,0.12)] p-8 shadow-xl">
          <div className="text-center mb-6">
            <img src="/logo.png" alt="InnovateFlow" className="w-14 h-14 rounded-xl mx-auto mb-3" />
            <div className="flex items-center justify-center gap-1.5">
              <h2 className="text-2xl font-bold text-iflow-900">Create your account</h2>
              <HelpPopover
                topic="signup-access"
                title="Who can create an account?"
                body="Anyone can create an individual account here, free for 30 days with no card. If you are setting up for an organisation, use Talk to us and we will arrange it with you."
              />
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Protect and analyse your projects. {OFFER_PHRASE}.
            </p>
          </div>

          {error && (
            <div ref={errorRef} role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* noValidate: the browser's own one-field bubble is replaced by the in-page
              message above, which names the problem in this form's own words. */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Full name */}
            <div>
              <label htmlFor="signup-name" className={labelClass}>
                Full name <span className="text-red-500">*</span>
              </label>
              <input id="signup-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                className={inputClass} placeholder="Dr. Amara Okafor" required autoComplete="name" />
            </div>

            {/* Work email */}
            <div>
              <label htmlFor="signup-email" className={labelClass}>
                Work email <span className="text-red-500">*</span>
              </label>
              <input id="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className={inputClass} placeholder="you@organisation.com.au" required autoComplete="email" />
              <p className="text-xs text-slate-500 mt-1">
                Use your organisation email so colleagues can find and share with you. This becomes your sign-in.
              </p>
            </div>

            {/* Password + strength + rules */}
            <div>
              <label htmlFor="signup-password" className={labelClass}>
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input id="signup-password" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} className={`${inputClass} pr-14`}
                  placeholder="Create a strong password" required autoComplete="new-password" />
                <button type="button" onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-iflow-600 hover:text-iflow-700">
                  {showPassword ? 'hide' : 'show'}
                </button>
              </div>
              {/* Strength meter — one bar per rule met */}
              <div className="mt-2 grid grid-cols-4 gap-1.5" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`h-1.5 rounded-full ${
                    i < rulesMet ? (allRulesMet ? 'bg-green-500' : rulesMet >= 2 ? 'bg-amber-400' : 'bg-red-400') : 'bg-slate-200'
                  }`} />
                ))}
              </div>
              {password.length > 0 && (
                <p className={`text-xs font-semibold mt-1 ${strengthColor}`}>{strengthLabel}</p>
              )}
              <div className="mt-2 rounded-lg bg-sky-500/5 border border-sky-200/60 p-3 space-y-1.5">
                {rules.map((r) => (
                  <div key={r.key} className="flex items-center gap-2 text-sm">
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${
                      r.ok ? 'bg-green-500/20 text-green-600' : 'bg-slate-200 text-slate-600'
                    }`}>
                      <Check size={11} strokeWidth={3} />
                    </span>
                    <span className={r.ok ? 'text-green-700' : 'text-slate-500'}>{r.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                Your password is stored only as a salted hash — we can never read it. All four rules must be met before we can create the account.
              </p>
            </div>

            {/* Confirm password */}
            <div>
              <label htmlFor="signup-confirm" className={labelClass}>
                Confirm password <span className="text-red-500">*</span>
              </label>
              <input id="signup-confirm" type={showPassword ? 'text' : 'password'} value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`${inputClass} ${showMismatch ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
                placeholder="Re-enter your password" required autoComplete="new-password" />
              {showMismatch && (
                <p className="text-xs text-red-600 mt-1">Passwords don&apos;t match — re-enter to confirm.</p>
              )}
            </div>

            {/* Organisation + Country */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="signup-org" className={labelClass}>
                  Organisation <span className="font-normal text-slate-600">(optional)</span>
                </label>
                <input id="signup-org" type="text" value={organisation} onChange={(e) => setOrganisation(e.target.value)}
                  className={inputClass} placeholder="Meridian Ventures" autoComplete="organization" />
              </div>
              <div>
                <label htmlFor="signup-country" className={labelClass}>Country of residence</label>
                <input id="signup-country" type="text" value={country} onChange={(e) => setCountry(e.target.value)}
                  className={inputClass} placeholder="Australia" autoComplete="country-name" />
              </div>
            </div>
            <p className="text-xs text-slate-500 -mt-2">
              Leave blank to start as an individual — you can join or create an org later.
            </p>

            {/* Plan chooser */}
            <div>
              <label className={labelClass}>Choose a starting plan</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setPlan('free')}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    plan === 'free' ? 'border-iflow-500 bg-iflow-50 ring-2 ring-iflow-200' : 'border-slate-300 hover:border-slate-400'
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-iflow-900">Free trial</span>
                    <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${plan === 'free' ? 'border-iflow-500 bg-iflow-500' : 'border-slate-300'}`} />
                  </div>
                  <span className="mt-1 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700">Recommended</span>
                  <p className="text-xs text-slate-500 mt-1">30 days · 10 analyses · 2 GB · no card</p>
                </button>
                <button type="button" onClick={() => setPlan('pro')}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    plan === 'pro' ? 'border-iflow-500 bg-iflow-50 ring-2 ring-iflow-200' : 'border-slate-300 hover:border-slate-400'
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-iflow-900">Professional</span>
                    <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${plan === 'pro' ? 'border-iflow-500 bg-iflow-500' : 'border-slate-300'}`} />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">A$49/mo · 100 analyses · 25 GB</p>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                You can upgrade or downgrade anytime from Account › Billing. Free needs no payment details.
              </p>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Creating account...
                </span>
              ) : (
                'Create account'
              )}
            </button>

            <p className="text-xs text-slate-500 text-center">
              By creating an account you agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-iflow-600 font-medium hover:text-iflow-700">Terms</a> and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-iflow-600 font-medium hover:text-iflow-700">Privacy Policy</a>.
            </p>
          </form>

          <p className="text-sm text-center text-slate-600 mt-5">
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="text-iflow-600 font-semibold hover:text-iflow-700">
              Sign in →
            </button>
          </p>

          <p className="text-sm text-center text-slate-600 mt-2">
            Setting up for an organisation?{' '}
            <a href={TALK_TO_US_HREF} className="text-iflow-600 font-semibold hover:text-iflow-700">Talk to us</a>
          </p>

          <div className="mt-5 flex items-start gap-2 rounded-lg bg-sky-500/5 border border-sky-200/60 p-3 text-xs text-iflow-800">
            <Lock size={14} className="text-iflow-600 flex-shrink-0 mt-0.5" />
            <span>
              Your intellectual property is encrypted and hosted entirely in{' '}
              <span className="font-semibold">Australia (ap-southeast-2)</span>. Nothing leaves the region, and only
              you hold the keys to your disclosures.
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
